import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import useAdminStore from '../../store/adminStore';
import useAppStore from '../../store/appStore';
import AktivCounterModal from './AktivCounterModal';

const IconBase = ({ children, size = 20, className = '' }: any) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {children}
    </svg>
);

const ActivityIcon = (props: any) => (
    <IconBase {...props}>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </IconBase>
);

const MS_ONLINE = 20 * 60 * 1000;
const MS_90DAYS = 90 * 24 * 60 * 60 * 1000;

const toMs = (raw: unknown): number | null => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw;
    if (typeof raw === 'string') {
        const parsed = Date.parse(raw);
        return Number.isNaN(parsed) ? null : parsed;
    }
    const anyRaw = raw as { seconds?: number; toDate?: () => Date };
    if (typeof anyRaw?.toDate === 'function') {
        const t = anyRaw.toDate().getTime();
        return Number.isNaN(t) ? null : t;
    }
    if (typeof anyRaw?.seconds === 'number') return anyRaw.seconds * 1000;
    return null;
};

const isSnoozed = (raw: unknown): boolean => {
    if (raw == null || raw === '') return false;
    const ts =
        typeof raw === 'number'
            ? raw
            : typeof raw === 'string'
              ? Date.parse(raw)
              : toMs(raw);
    return ts != null && !Number.isNaN(ts) && ts > Date.now();
};

/**
 * AktivCounter als Sidebar-Radar — neben Sentinel / Stripe / Change.
 * Klick öffnet die Liste (Standardfilter: Akut > 90 Tage).
 */
const AktivCounterRadar: React.FC = () => {
    const crmClients = useAdminStore((s) => s.crmClients);
    const pendingApprovals = useAdminStore((s) => s.pendingApprovals);
    const team = useAppStore((s) => s.team || []);
    const [isOpen, setIsOpen] = useState(false);
    const [teamOnlineCount, setTeamOnlineCount] = useState(0);

    const acuteCount = useMemo(() => {
        const now = Date.now();
        const seen = new Set<string>();
        let n = 0;
        [...(crmClients || []), ...(pendingApprovals || [])].forEach((c: any) => {
            if (!c?.id || seen.has(c.id)) return;
            seen.add(c.id);
            if (c.isArchived || c.isDeleted || c.isActive === false) return;
            // Vorabkunden und ohne erfasste Aktivität nicht auswerten
            if (c.isLegacy === true || c.clientData?.isLegacy === true) return;
            if (isSnoozed(c.snoozedUntil || c.clientData?.snoozedUntil)) return;
            const ts = toMs(c.lastActiveAt || c.clientData?.lastActiveAt);
            if (ts == null) return;
            if (now - ts >= MS_90DAYS) n += 1;
        });
        return n;
    }, [crmClients, pendingApprovals]);

    // Team online → Badge am Counter (nicht in der Mandantenansicht)
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const emails = new Set(
                    (team || [])
                        .map((m: any) => String(m?.email || '').trim().toLowerCase())
                        .filter(Boolean)
                );
                const ids = new Set(
                    (team || []).map((m: any) => String(m?.id || '').trim()).filter(Boolean)
                );
                if (emails.size === 0 && ids.size === 0) {
                    if (!cancelled) setTeamOnlineCount(0);
                    return;
                }
                const snap = await getDocs(collection(db, 'users'));
                const now = Date.now();
                const seen = new Set<string>();
                let n = 0;
                snap.docs.forEach((d) => {
                    const data = d.data() as any;
                    const em = String(data.email || '').trim().toLowerCase();
                    const match = ids.has(d.id) || (em && emails.has(em));
                    if (!match) return;
                    const key = em || d.id;
                    if (seen.has(key)) return;
                    const ts = toMs(data.lastActiveAt || data.lastAktePresence?.at);
                    if (ts == null || now - ts > MS_ONLINE) return;
                    seen.add(key);
                    n += 1;
                });
                if (!cancelled) setTeamOnlineCount(n);
            } catch {
                if (!cancelled) setTeamOnlineCount(0);
            }
        };
        void load();
        const t = window.setInterval(() => void load(), 60 * 1000);
        return () => {
            cancelled = true;
            window.clearInterval(t);
        };
    }, [team]);

    const hasAcute = acuteCount > 0;

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all shadow-lg group ${
                    hasAcute
                        ? 'bg-amber-50 border-amber-500 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/10 dark:border-amber-500/50 dark:hover:bg-amber-900/20 dark:text-amber-400'
                        : 'bg-pe-card border-pe-border-strong text-pe-text hover:bg-pe-hover'
                }`}
            >
                <div className="flex items-center gap-3">
                    <ActivityIcon
                        size={18}
                        className={hasAcute ? 'text-amber-600 dark:text-amber-400 animate-pulse' : 'text-pe-muted'}
                    />
                    <span className={`text-sm font-bold ${hasAcute ? 'text-amber-800 dark:text-amber-400' : 'text-pe-text'}`}>
                        AktivCounter
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    {teamOnlineCount > 0 && (
                        <span className="bg-emerald-500 text-black text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_10px_rgba(16,185,129,0.45)]">
                            {teamOnlineCount} online
                        </span>
                    )}
                    {hasAcute && (
                        <span className="bg-amber-500 text-black text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_10px_rgba(245,158,11,0.45)]">
                            {acuteCount}
                        </span>
                    )}
                </div>
            </button>

            <AktivCounterModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                initialFilter={hasAcute ? 'acute90' : teamOnlineCount > 0 ? 'online' : 'all'}
            />
        </>
    );
};

export default AktivCounterRadar;
